import axios from "axios";
import { createAsyncThunk, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import helpers from "helpers";
import { createEntitySlice, serializeAxiosError } from "../config/reducer.utils";
import { TypedCity } from "../interface/city.model";

/**
 *   Reducer used for front-end, with city.model.ts
 *   Interface.ts can be use in both front-end and back-end! But prefer using city.model.ts
 */

const initialState = {
  loading: false,
  errorMessage: [] as any,
  entities: [],
  entity: null,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  searchCities: "",
};

const apiUrl = "city";

// Actions

export const getEntities = createAsyncThunk("city/fetch_entity_list", async (object: any) => {
  const EndURL = helpers.buildEndUrl(object);
  const requestUrl = `${apiUrl}/list${EndURL}`;
  return axios.get<any>(requestUrl);
});

export const searchCities = createAsyncThunk("city/fetch_entity_list", async (value: string | number) => {
  const requestUrl = `${apiUrl}/list?search=${value}&page=1&limit=10`;
  return axios.get<any>(requestUrl);
});

export const searchUsers = createAsyncThunk("users/fetch_entity_list", async (value: string | number) => {
  const requestUrl = `${apiUrl}/list/admin?search=${value}&page=1&limit=100`;
  return axios.get<any>(requestUrl);
});

export const getEntity = createAsyncThunk(
  "city/fetch_entity",
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/detail/${id}`;
    return axios.get<any>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  "city/create_entity",
  async (entity: TypedCity, thunkAPI) => {
    const result = await axios.post<TypedCity>(`${apiUrl}/create`, helpers.cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  "city/update_entity",
  async (entity: TypedCity, thunkAPI) => {
    const result = await axios.patch<TypedCity>(`${apiUrl}/update`, helpers.cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  "city/partial_update_entity",
  async (entity: TypedCity, thunkAPI) => {
    const result = await axios.patch<TypedCity>(`${apiUrl}/${entity._id}`, helpers.cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  "city/delete_entity",
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}delete-city/${id}`;
    thunkAPI.dispatch(getEntities({}));
    return axios.delete<any>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

// slice

export const Reducer_City = createEntitySlice({
  name: "city",
  initialState,
  reducers: {
    clearError: (state) => {
      state.errorMessage = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntity.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
      })
      .addCase(createEntity.rejected, (state, action) => {
        state.loading = false;
        state.entity = null;
        state.errorMessage = action.payload;
      })

      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers["x-total-count"], 10),
        };
      })
      .addMatcher(isRejected(createEntity, updateEntity, partialUpdateEntity, deleteEntity), (state, action) => {
        state.loading = false;
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.payload;
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(deleteEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = null;
      })
      .addMatcher(isPending(deleteEntity), (state) => {
        state.updating = true;
        state.loading = true;
        state.updateSuccess = false;
        state.entity = null;
      })
      .addMatcher(isPending(getEntities, getEntity), (state) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity), (state) => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { clearError, reset } = Reducer_City.actions;

// Reducer
export default Reducer_City.reducer;

